import {
  CURATED_ROUTE_TEMPLATES,
  matchTemplateByName,
  type CuratedRouteTemplate,
} from "../catalog/curated-route-templates";
import {
  corridorById,
  corridorForTemplate,
  findCorridorInText,
  type CuratedCorridor,
} from "../catalog/corridor-routes";
import { buildRouteSkeleton, isRoadTemplate, type RouteSkeleton } from "./route-skeleton";

export type ProGenerationPreflightStatus =
  | "ready"
  | "route_mode_required"
  | "route_not_feasible";

export type ProGenerationPreflight = {
  status: ProGenerationPreflightStatus;
  canStartGeneration: boolean;
  /** Só fica true depois de todas as escolhas gratuitas estarem resolvidas. */
  canReserveQuota: boolean;
  template?: CuratedRouteTemplate;
  corridor?: CuratedCorridor;
  skeleton?: RouteSkeleton;
  message?: string;
};

function templateFromCorridor(corridor: CuratedCorridor): CuratedRouteTemplate {
  const minDays = corridor.durationPresets.reduce(
    (minimum, preset) => Math.min(minimum, preset.days),
    corridor.durationPresets[0]?.days ?? 2,
  );
  return {
    id: `corridor-${corridor.id}`,
    version: 1,
    active: true,
    countryCodes: [...corridor.countryCodes],
    regions: [],
    category: "road_trip",
    name: corridor.name,
    aliases: [...corridor.aliases],
    anchors: corridor.waypoints.map((waypoint) => waypoint.name),
    requiredPlaces: [
      corridor.waypoints[0]?.name,
      corridor.waypoints[corridor.waypoints.length - 1]?.name,
    ].filter((name): name is string => Boolean(name)),
    approxHours: Math.max(1, Math.round(corridor.approxKm / 55)),
    approxKm: corridor.approxKm,
    minTripDays: minDays,
    maxTripDays: corridor.durationPresets.at(-1)?.days,
    transport: "car",
    officialUrl: corridor.officialUrl,
    verifiedAt: corridor.verifiedAt,
  };
}

function resolveTemplate(input: {
  prompt: string;
  templateId?: string;
  corridorId?: string;
}): { template?: CuratedRouteTemplate; corridor?: CuratedCorridor } {
  const explicitTemplate = input.templateId
    ? CURATED_ROUTE_TEMPLATES.find((candidate) => candidate.active && candidate.id === input.templateId)
    : undefined;
  const namedTemplate = explicitTemplate ?? matchTemplateByName(input.prompt);
  const corridor =
    corridorById(input.corridorId) ??
    corridorForTemplate(namedTemplate?.id ?? input.templateId) ??
    findCorridorInText(input.prompt) ??
    undefined;
  const catalogTemplate =
    namedTemplate ??
    (corridor?.templateId
      ? CURATED_ROUTE_TEMPLATES.find(
          (candidate) => candidate.active && candidate.id === corridor.templateId,
        )
      : undefined);
  return { template: catalogTemplate ?? (corridor ? templateFromCorridor(corridor) : undefined), corridor };
}

/**
 * Gate puro e gratuito. Tem de correr antes do registo da tentativa e antes
 * da reserva de quota no servidor.
 */
export function preflightProGeneration(input: {
  prompt: string;
  days: number;
  templateId?: string;
  corridorId?: string;
  templateMode?: "full" | "partial";
  startRegion?: string;
}): ProGenerationPreflight {
  const { template, corridor } = resolveTemplate(input);
  const requiresRouteMode = Boolean(template && isRoadTemplate(template));
  if (requiresRouteMode && !input.templateMode) {
    return {
      status: "route_mode_required",
      canStartGeneration: false,
      canReserveQuota: false,
      template,
      corridor,
      message: `Escolhe entre o percurso completo de ${template?.name} e apenas um troço antes de gerar.`,
    };
  }

  const skeleton =
    template && input.templateMode
      ? buildRouteSkeleton({
          template,
          days: input.days,
          mode: input.templateMode,
          startRegion: input.startRegion,
        })
      : undefined;
  if (skeleton && !skeleton.feasible) {
    return {
      status: "route_not_feasible",
      canStartGeneration: false,
      canReserveQuota: false,
      template,
      corridor,
      skeleton,
      message: skeleton.reason,
    };
  }

  return {
    status: "ready",
    canStartGeneration: true,
    canReserveQuota: true,
    template,
    corridor,
    skeleton,
  };
}
